/**
 * TEMPORARY: in-memory stand-in for the organization exports API while the
 * backend endpoint is built. Enabled with REACT_APP_DATA_EXPORT_MOCK=true
 * (seeded history) or =empty (no history). Remove before merging to main,
 * together with DataExportsMockData.ts.
 *
 * It answers with the same DTOs the real endpoint will return, so the
 * converter, thunks and UI run exactly as they will against the backend.
 * Status moves with time: pending → processing after 2s → completed after 9s.
 * Row counts and files come from DataExportsMockData, filtered by the export's
 * locations and date range, so the count in the list matches the CSV.
 */
import moment from 'moment-timezone';
import { MAX_RUNNING_DATA_EXPORTS } from 'model/dataExports';
import { CreateDataExportDto, DataExportDto } from './DataExportsDtos';
import { MockLocation, buildMockCsvUrl, countMockRows } from './DataExportsMockData';

export type { MockLocation } from './DataExportsMockData';

export const isDataExportsMockEnabled = (): boolean =>
    ['true', 'empty'].includes(`${process.env.REACT_APP_DATA_EXPORT_MOCK}`);

const PROCESSING_AFTER_MS = 2000;
const COMPLETED_AFTER_MS = 9000;
const NETWORK_DELAY_MS = 450;

interface MockExport {
    dto: DataExportDto;
    createdAtMs: number;
    // Locations the file was built for; rebuilt when the organization's locations load or change.
    fileKey?: string;
}

const delay = () => new Promise(resolve => setTimeout(resolve, NETWORK_DELAY_MS));

const seed = (): Array<MockExport> => {
    const now = moment();
    const finished = (
        id: string,
        resource: string,
        hoursAgo: number,
        filters: DataExportDto['filters']
    ): MockExport => {
        const createdAt = now.clone().subtract(hoursAgo, 'hours');
        const completedAt = createdAt.clone().add(2, 'minutes');
        return {
            createdAtMs: createdAt.valueOf(),
            dto: {
                id,
                resource,
                format: 'csv',
                status: 'completed',
                row_count: null,
                filters,
                requested_by: { id: 1, name: 'Ana López' },
                created_at: createdAt.toISOString(),
                completed_at: completedAt.toISOString(),
                url_expires_at: completedAt.clone().add(24, 'hours').toISOString(),
                download_url: null,
                failure_reason: null
            }
        };
    };

    return [
        finished('mock-seed-1', 'customers', 3, { marketplace_ids: [] }),
        finished('mock-seed-2', 'invoices', 6, {
            marketplace_ids: [],
            start_date: now.clone().startOf('year').format('YYYY-MM-DD'),
            end_date: now.format('YYYY-MM-DD')
        }),
        finished('mock-seed-4', 'pets', 52, { marketplace_ids: [] })
    ];
};

let mockExports: Array<MockExport> | null = null;

const store = (): Array<MockExport> => {
    if (mockExports === null) {
        mockExports = process.env.REACT_APP_DATA_EXPORT_MOCK === 'empty' ? [] : seed();
    }
    return mockExports;
};

// Fills in the row count and file of a completed export from the mock dataset.
const attachFile = (mock: MockExport, locations: Array<MockLocation>) => {
    const fileKey = locations.map(location => location.id).join(',');
    if (mock.dto.status !== 'completed' || mock.fileKey === fileKey) return;

    if (mock.dto.download_url?.startsWith('blob:')) {
        URL.revokeObjectURL(mock.dto.download_url);
    }

    mock.fileKey = fileKey;
    mock.dto = {
        ...mock.dto,
        row_count: countMockRows(mock.dto.resource, mock.dto.filters, locations),
        download_url: buildMockCsvUrl(mock.dto.resource, mock.dto.filters, locations)
    };
};

// Moves the exports still running forward according to how long ago they were created.
const advance = (mock: MockExport, locations: Array<MockLocation>): DataExportDto => {
    if (mock.dto.status === 'pending' || mock.dto.status === 'processing') {
        const elapsed = Date.now() - mock.createdAtMs;

        if (elapsed >= COMPLETED_AFTER_MS) {
            const completedAt = moment();
            mock.dto = {
                ...mock.dto,
                status: 'completed',
                completed_at: completedAt.toISOString(),
                url_expires_at: completedAt.clone().add(24, 'hours').toISOString()
            };
        } else if (elapsed >= PROCESSING_AFTER_MS) {
            mock.dto = { ...mock.dto, status: 'processing' };
        }
    }

    attachFile(mock, locations);
    return mock.dto;
};

export const mockFetchDataExports = async (locations: Array<MockLocation>): Promise<Array<DataExportDto>> => {
    await delay();
    return store()
        .map(mock => advance(mock, locations))
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
};

export const mockCreateDataExport = async (
    payload: CreateDataExportDto,
    requestedBy: string,
    locations: Array<MockLocation>
): Promise<DataExportDto> => {
    await delay();

    const running = store()
        .map(mock => advance(mock, locations))
        .filter(dto => dto.status === 'pending' || dto.status === 'processing');

    if (running.length >= MAX_RUNNING_DATA_EXPORTS) {
        // Same shape as the backend's 409 so the thunk's error handling is exercised.
        throw {
            response: {
                status: 409,
                data: { error: `You can run up to ${MAX_RUNNING_DATA_EXPORTS} exports at a time.` }
            }
        };
    }

    const now = moment();
    const mock: MockExport = {
        createdAtMs: now.valueOf(),
        dto: {
            id: `mock-${now.valueOf()}`,
            resource: payload.resource,
            format: payload.format,
            status: 'pending',
            row_count: null,
            filters: {
                marketplace_ids: payload.marketplace_ids,
                start_date: payload.start_date,
                end_date: payload.end_date
            },
            requested_by: { id: 0, name: requestedBy },
            created_at: now.toISOString(),
            completed_at: null,
            url_expires_at: null,
            download_url: null,
            failure_reason: null
        }
    };

    store().unshift(mock);
    return mock.dto;
};
